import { noop } from 'lodash';
import { createContext, PropsWithChildren, useState } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useGuardedContext } from '@/hooks/useGuardedContext';

import {
  EntityOrClientProfileId,
  EstateWaterfallHypotheticalCharitableTrustModalContextType,
  HypotheticalCharitableTrustModalKind,
  SourceValues,
} from './EstateWaterfallHypotheticalCharitableTrustModal.types';

const DEFAULT_IS_LOADING = true;

const EstateWaterfallHypotheticalCharitableTrustModalContext =
  createContext<EstateWaterfallHypotheticalCharitableTrustModalContextType>({
    waterfallId: '',
    hypotheticalCharitableTrustId: '',
    isLoading: DEFAULT_IS_LOADING,
    setIsLoading: noop,
    initialSourceId: undefined,
    proposalOptions: [],
    setProposalOptions: noop,
    sourceEntityOptions: [],
    setSourceEntityOptions: noop,
    sourceIndividualOptions: [],
    setSourceIndividualOptions: noop,
    sourceValues: {},
    setSourceValues: noop,
    fundingId: '',
    setFundingId: noop,
    charitableTrustModalKind: HypotheticalCharitableTrustModalKind.CRT,
  });

export interface EstateWaterfallHypotheticalCharitableTrustModalContextProviderProps
  extends PropsWithChildren {
  waterfallId: string;
  hcrtId?: string | null;
  hcltId?: string | null;
  initialSourceId?: string;
  charitableTrustModalKind: HypotheticalCharitableTrustModalKind;
}

export function EstateWaterfallHypotheticalCharitableTrustModalContextProvider(
  props: EstateWaterfallHypotheticalCharitableTrustModalContextProviderProps
) {
  const { hcrtId, hcltId, charitableTrustModalKind } = props;

  const [isLoading, setIsLoading] = useState(DEFAULT_IS_LOADING);
  const [proposalOptions, setProposalOptions] = useState<
    TypeaheadSelectInputOption<string>[]
  >([]);
  const [sourceEntityOptions, setSourceEntityOptions] = useState<
    TypeaheadSelectInputOption<string>[]
  >([]);
  const [sourceIndividualOptions, setSourceIndividualOptions] = useState<
    TypeaheadSelectInputOption<string>[]
  >([]);
  const [sourceValues, setSourceValues] = useState<
    Record<EntityOrClientProfileId, SourceValues>
  >({});
  const [fundingId, setFundingId] = useState('');

  const hypotheticalCharitableTrustId = hcrtId || hcltId || '';

  return (
    <EstateWaterfallHypotheticalCharitableTrustModalContext.Provider
      value={{
        waterfallId: props.waterfallId,
        hypotheticalCharitableTrustId,
        isLoading,
        setIsLoading,
        initialSourceId: props.initialSourceId,
        proposalOptions,
        setProposalOptions,
        sourceEntityOptions,
        setSourceEntityOptions,
        sourceIndividualOptions,
        setSourceIndividualOptions,
        sourceValues,
        setSourceValues,
        fundingId,
        setFundingId,
        charitableTrustModalKind,
      }}
    >
      {props.children}
    </EstateWaterfallHypotheticalCharitableTrustModalContext.Provider>
  );
}

export function useEstateWaterfallHypotheticalCharitableTrustModalContext() {
  return useGuardedContext(
    EstateWaterfallHypotheticalCharitableTrustModalContext,
    'EstateWaterfallHypotheticalCharitableTrustModalContextProvider'
  );
}
